import { API_BASE } from './base'

export type Role = 'owner' | 'admin' | 'member' | 'viewer'

export interface AuthIdentity {
  user: {
    id: number
    email: string
    display_name: string
    is_platform_admin: boolean
  }
  organization: {
    id: number
    name: string
    slug: string
    role: Role
  }
  organizations?: Array<{
    id: number
    name: string
    slug: string
    role: Role
  }>
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.detail || `请求失败 (${response.status})`)
  }
  return response.status === 204 ? (undefined as T) : response.json()
}

export const authApi = {
  me: () => jsonRequest<AuthIdentity>('/auth/me'),
  login: (data: { email: string; password: string }) =>
    jsonRequest<AuthIdentity>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  register: (data: {
    email: string
    verification_token: string
    password: string
    display_name: string
    organization_name?: string
    invitation_token?: string
  }) =>
    jsonRequest<AuthIdentity>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  sendRegistrationCode: (email: string) =>
    jsonRequest<{ status: string; expires_in: number; resend_after: number; debug_code?: string }>(
      '/auth/email-verification/send',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),
  verifyRegistrationCode: (email: string, code: string) =>
    jsonRequest<{ verification_token: string; expires_in: number }>(
      '/auth/email-verification/verify',
      { method: 'POST', body: JSON.stringify({ email, code }) },
    ),
  logout: () => jsonRequest<void>('/auth/logout', { method: 'POST' }),
  switchOrganization: (organizationId: number) =>
    jsonRequest<AuthIdentity>(`/auth/switch-organization/${organizationId}`, {
      method: 'POST',
    }),
  members: () =>
    jsonRequest<{ items: OrganizationMember[] }>('/organization/members'),
  invite: (data: { email: string; role: Exclude<Role, 'owner'> }) =>
    jsonRequest<InvitationResult>('/organization/invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  acceptInvitation: (token: string) =>
    jsonRequest<{ status: string; organization_id: number }>(
      '/organization/invitations/accept',
      { method: 'POST', body: JSON.stringify({ token }) },
    ),
  updateMember: (userId: number, data: { role?: string; status?: string }) =>
    jsonRequest<OrganizationMember>(`/organization/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  updateOrganization: (name: string) =>
    jsonRequest<{ id: number; name: string; slug: string }>('/organization', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  platformOrganizations: () =>
    jsonRequest<{ items: PlatformOrganization[] }>('/platform/organizations'),
  platformUsers: () =>
    jsonRequest<{ items: PlatformUser[] }>('/platform/users'),
}

export interface OrganizationMember {
  user_id: number
  email: string
  display_name: string
  role: Role
  status: string
  joined_at: string
}

export interface InvitationResult {
  id: number
  email: string
  role: Role
  expires_at: string
  delivery: 'email'
}

export interface PlatformOrganization {
  id: number
  name: string
  slug: string
  status: string
  member_count: number
  created_at: string
}

export interface PlatformUser {
  id: number
  email: string
  display_name: string
  status: string
  is_platform_admin: boolean
  created_at: string
}
